import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Gavel, MapPin, RefreshCw, Calendar, Clock, FileText, ExternalLink,
    Users, X, Lock, Sparkles, AlertTriangle, Landmark, ScrollText, Flame,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

const eur = (n) => (n === null || n === undefined ? "—" : `€ ${Number(n).toLocaleString("it-IT")}`);
const fmtDate = (d) => {
    if (!d) return "Data da definire";
    try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return d; }
};

export default function CrmAste({ onGoToZones }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(false);

    const loadStatus = useCallback(async () => {
        try { const r = await api.get("/crm/aste/status"); setStatus(r.data); }
        catch (e) { setStatus({ active: false, price_monthly: 79, price_annual: 818, annual_savings: 130, currency: "eur" }); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const activate = async (interval) => {
        setActivating(true);
        try {
            const r = await api.post("/crm/aste/checkout", { origin_url: PUBLIC_SITE_URL, interval });
            window.location.href = r.data.url;
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore attivazione modulo");
            setActivating(false);
        }
    };

    if (loading) return <div className="text-ink-400 text-sm py-12 text-center" data-testid="aste-loading">Caricamento…</div>;

    return (
        <div data-testid="crm-aste-page">
            <div className="mb-8">
                <span className="label-eyebrow text-gold">VD Manager · Acquisizione</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <Gavel size={22} className="text-gold" /> Radar Aste
                </h2>
                <p className="text-ink-400 text-sm mt-1">Ricerca automatica di aste immobiliari giudiziarie e notarili nelle tue Zone Operative, con match automatico sui tuoi Lead.</p>
            </div>

            {status?.active ? (
                <ActiveAste status={status} onGoToZones={onGoToZones} onCancelled={loadStatus} />
            ) : (
                <PaywallView
                    activating={activating}
                    onActivate={activate}
                    priceMonthly={status?.price_monthly ?? 79}
                    priceAnnual={status?.price_annual ?? 818}
                    annualSavings={status?.annual_savings ?? 130}
                />
            )}
        </div>
    );
}

function PaywallView({ activating, onActivate, priceMonthly, priceAnnual, annualSavings }) {
    const [billingInterval, setBillingInterval] = useState("year");
    return (
        <div className="relative border border-white/10 overflow-hidden min-h-[560px]">
            {/* Contenuto sfocato di anteprima */}
            <div className="blur-locked p-6 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[560px]">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="border border-white/10 p-4 flex gap-4">
                        <div className="w-24 h-20 bg-ink-900 shrink-0" />
                        <div className="flex-1 space-y-2">
                            <div className="h-3 bg-ink-900 w-3/4" />
                            <div className="h-3 bg-ink-900 w-1/2" />
                            <div className="h-3 bg-ink-900 w-1/3" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Overlay sblocco */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm p-8 py-12 text-center" data-testid="aste-paywall">
                <div className="w-14 h-14 border border-gold/50 bg-gold/10 flex items-center justify-center mb-4">
                    <Lock size={22} className="text-gold" />
                </div>
                <span className="label-eyebrow text-gold mb-2">Add-on VD Manager</span>
                <h3 className="font-serif text-2xl text-ink-50 mb-3">Modulo Aste Professional</h3>
                <p className="text-ink-400 text-sm max-w-md mb-6 leading-relaxed">
                    Ricerca automatica di aste immobiliari giudiziarie e notarili nelle tue Zone Operative, aggiornata ogni giorno.
                    Ogni asta trovata viene incrociata con il database Lead della tua agenzia: sai subito quali clienti contattare.
                </p>

                {/* Selettore piano mensile / annuale */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md mb-6" data-testid="aste-plan-selector">
                    <button
                        type="button"
                        data-testid="aste-plan-month"
                        onClick={() => setBillingInterval("month")}
                        className={`vd-card p-4 text-left transition-all ${billingInterval === "month" ? "ring-2 ring-gold" : "opacity-80 hover:opacity-100"}`}
                    >
                        <div className="text-[10px] uppercase tracking-wider text-ink-400">Mensile</div>
                        <div className="font-serif text-2xl text-ink-50 mt-1">€{priceMonthly}<span className="text-xs text-ink-400 font-sans">/mese</span></div>
                        <div className="text-[11px] text-ink-500 mt-1">Disdici quando vuoi</div>
                    </button>
                    <button
                        type="button"
                        data-testid="aste-plan-year"
                        onClick={() => setBillingInterval("year")}
                        className={`vd-card p-4 text-left relative transition-all ${billingInterval === "year" ? "ring-2 ring-gold" : "opacity-80 hover:opacity-100"}`}
                    >
                        <span data-testid="aste-plan-year-badge" className="absolute -top-2.5 right-3 bg-gold text-black text-[9px] font-bold px-2.5 py-1 uppercase tracking-wide rounded-full shadow-lg">
                            Risparmia €{annualSavings}
                        </span>
                        <div className="text-[10px] uppercase tracking-wider text-ink-400">Annuale · Consigliato</div>
                        <div className="font-serif text-2xl text-ink-50 mt-1">€{priceAnnual}<span className="text-xs text-ink-400 font-sans">/anno</span></div>
                        <div className="text-[11px] text-emerald-400 mt-1 font-medium">≈ €{Math.round(priceAnnual / 12)}/mese</div>
                    </button>
                </div>

                <button
                    data-testid="aste-activate-btn"
                    onClick={() => onActivate(billingInterval)}
                    disabled={activating}
                    className="cta-gold px-8 py-3 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                    <Sparkles size={14} /> {activating ? "Attivazione…" : "ATTIVA ORA"}
                </button>
            </div>
        </div>
    );
}

function ActiveAste({ status, onGoToZones, onCancelled }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMsg, setRefreshMsg] = useState("");
    const [matchesFor, setMatchesFor] = useState(null);
    const [showCancel, setShowCancel] = useState(false);
    const [publicationFilter, setPublicationFilter] = useState("24h");
    const [auctionDays, setAuctionDays] = useState("90");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/crm/aste", { params: { publication_filter: publicationFilter, auction_days: auctionDays } });
            setData(r.data);
        }
        catch (e) { setData({ items: [], zone_operative: [] }); }
        finally { setLoading(false); }
    }, [publicationFilter, auctionDays]);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        setRefreshing(true); setRefreshMsg("");
        try {
            const r = await api.post("/crm/aste/refresh", {}, { params: { publication_filter: publicationFilter, auction_days: auctionDays } });
            if (r.data.grounding_unavailable) {
                setRefreshMsg("Ricerca momentaneamente non disponibile (crediti esauriti). Riprova più tardi.");
            } else {
                setRefreshMsg(`Aggiornate ${r.data.synced_cities?.length || 0} città · ${r.data.total_listings} aste totali`);
            }
            await load();
        } catch (e) {
            setRefreshMsg(e?.response?.data?.detail || "Errore aggiornamento");
        } finally { setRefreshing(false); }
    };

    const zones = data?.zone_operative || [];

    return (
        <div data-testid="crm-aste-active" className="space-y-6">
            {data?.grounding_unavailable && (
                <div className="border border-amber-500/40 bg-amber-900/10 text-amber-300 text-xs p-3" data-testid="aste-grounding-warning">
                    Ricerca aste momentaneamente non disponibile (crediti di ricerca esauriti). I dati verranno aggiornati automaticamente non appena il servizio sarà di nuovo disponibile.
                </div>
            )}
            {/* Header controls */}
            <div className="aste-filter-panel flex flex-wrap items-center justify-between gap-4 p-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <MapPin size={14} className="text-gold" />
                    {zones.length === 0 ? (
                        <button onClick={onGoToZones} className="text-xs text-amber-300 hover:underline">Configura le Zone Operative per iniziare la ricerca →</button>
                    ) : (
                        zones.map((z) => (
                            <span key={z} className="text-[11px] border border-gold/40 bg-gold/10 text-gold px-2 py-1">{z}</span>
                        ))
                    )}
                </div>
                <div className="flex items-end gap-3 flex-wrap justify-end">
                    <div className="aste-filter-group">
                        <span>Data pubblicazione</span>
                        <Select value={publicationFilter} onValueChange={setPublicationFilter} disabled={refreshing}>
                            <SelectTrigger data-testid="aste-publication-filter" className="aste-filter-trigger"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="24h" data-testid="aste-publication-24h">Ultime 24h</SelectItem>
                                <SelectItem value="48h" data-testid="aste-publication-48h">Ultime 48h</SelectItem>
                                <SelectItem value="week" data-testid="aste-publication-week">Ultima settimana</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="aste-filter-group">
                        <span>Data asta</span>
                        <Select value={auctionDays} onValueChange={setAuctionDays} disabled={refreshing}>
                            <SelectTrigger data-testid="aste-date-filter" className="aste-filter-trigger"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="30" data-testid="aste-date-30">Prossimi 30 giorni</SelectItem>
                                <SelectItem value="60" data-testid="aste-date-60">Prossimi 60 giorni</SelectItem>
                                <SelectItem value="90" data-testid="aste-date-90">Prossimi 90 giorni</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {refreshMsg && <span data-testid="aste-refresh-message" className="text-[11px] text-ink-400">{refreshMsg}</span>}
                    <button
                        data-testid="aste-refresh-btn"
                        onClick={refresh}
                        disabled={refreshing || zones.length === 0}
                        className="cta-outline px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Aggiornamento…" : "Aggiorna ora"}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-ink-400 text-sm py-12 text-center">Caricamento aste…</div>
            ) : (data?.items || []).length === 0 ? (
                <div className="border border-white/10 border-dashed p-10 text-center text-sm text-ink-400" data-testid="aste-empty">
                    Nessuna asta trovata al momento nelle tue zone operative.<br />
                    <span className="text-xs opacity-70">La ricerca si aggiorna automaticamente ogni giorno, oppure premi &quot;Aggiorna ora&quot;.</span>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="aste-grid">
                    {data.items.map((it) => (
                        <AuctionCard
                            key={it.id}
                            item={it}
                            onOpenMatches={() => setMatchesFor(it)}
                        />
                    ))}
                </div>
            )}

            {/* Gestione abbonamento */}
            <div className="border border-white/10 p-4 flex flex-wrap items-center justify-between gap-3 text-xs text-ink-400">
                <div data-testid="aste-active-summary">
                    Modulo attivo{status.interval && <> · piano {status.interval === "year" ? "annuale" : "mensile"}</>} {status.expires_at && <>· rinnovo {status.auto_renew && !status.cancel_requested ? "automatico" : "disattivato"} il {fmtDate(status.expires_at)}</>}
                    {status.cancel_requested && <span className="text-amber-300 ml-2">(disdetta registrata)</span>}
                </div>
                {!status.cancel_requested && (
                    <button data-testid="aste-cancel-btn" onClick={() => setShowCancel(true)} className="text-red-300 hover:underline">Disdici abbonamento</button>
                )}
            </div>

            {matchesFor && <MatchesModal item={matchesFor} onClose={() => setMatchesFor(null)} />}
            {showCancel && (
                <CancelModal
                    onClose={() => setShowCancel(false)}
                    onConfirmed={() => { setShowCancel(false); onCancelled(); }}
                />
            )}
        </div>
    );
}

function AuctionCard({ item, onOpenMatches }) {
    const typeIcon = item.auction_type === "notarile" ? <ScrollText size={11} /> : <Landmark size={11} />;
    const [imageFailed, setImageFailed] = useState(false);
    return (
        <div data-testid={`aste-card-${item.id}`} className="aste-card-neu flex flex-col">
            <div className="flex gap-4 p-4">
                {item.cover_image_url && !imageFailed ? (
                    <img
                        data-testid={`aste-lot-image-${item.id}`}
                        src={item.cover_image_url}
                        alt={`Foto del lotto: ${item.title}`}
                        onError={() => setImageFailed(true)}
                        className="w-24 h-20 object-cover shrink-0 border border-white/10"
                    />
                ) : (
                    <div data-testid={`aste-lot-image-missing-${item.id}`} className="aste-image-placeholder">
                        <Gavel size={21} />
                        <span>Foto non disponibile</span>
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                        <div className="font-serif text-base text-ink-50 leading-snug min-w-0 flex-1">{item.title}</div>
                        {item.is_new && <span data-testid={`aste-new-${item.id}`} className="aste-new-badge"><Flame size={11} /> NEW</span>}
                    </div>
                    <div className="text-[11px] text-ink-400 mt-1 flex items-center gap-1.5 flex-wrap">
                        <MapPin size={10} /> {item.city}{item.address_hint ? ` · ${item.address_hint}` : ""}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 mt-1.5 flex items-center gap-1">
                        {typeIcon} {item.auction_type} · {item.court_or_source || "fonte non specificata"}
                    </div>
                    {(item.lot_identifier || item.procedure_number) && (
                        <div className="text-[10px] text-ink-400 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                            {item.lot_identifier && <span data-testid={`aste-lot-id-${item.id}`}>Lotto: {item.lot_identifier}</span>}
                            {item.procedure_number && <span data-testid={`aste-procedure-id-${item.id}`}>Procedura: {item.procedure_number}</span>}
                        </div>
                    )}
                </div>
            </div>
            <div className="border-t border-white/10 px-4 py-3 flex items-center justify-between gap-3">
                <div>
                    <div className="font-serif text-lg text-price">{eur(item.price_base)}</div>
                    {item.price_min_offer && item.price_min_offer !== item.price_base && (
                        <div className="text-[10px] text-ink-500">Offerta min. {eur(item.price_min_offer)}</div>
                    )}
                </div>
                <div className="text-right text-[11px] text-ink-400">
                    <div data-testid={`aste-date-label-${item.id}`} className={`flex items-center gap-1 justify-end ${item.auction_date ? "" : "text-amber-300 font-medium"}`}><Calendar size={10} /> {fmtDate(item.auction_date)}</div>
                    {item.auction_time && <div className="flex items-center gap-1 justify-end mt-0.5"><Clock size={10} /> {item.auction_time}</div>}
                </div>
            </div>
            <div className="border-t border-white/10 px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                    <span
                        data-testid={`aste-link-type-${item.id}`}
                        className={`aste-link-type-badge ${item.link_type === "direct_lot" ? "aste-link-direct" : "aste-link-procedure"}`}
                    >
                        <ExternalLink size={9} /> {item.link_label || (item.link_type === "direct_lot" ? "Scheda lotto" : "Link alla procedura")}
                    </span>
                    {item.perizia_url && (
                    <a href={item.perizia_url} target="_blank" rel="noreferrer" data-testid={`aste-perizia-${item.id}`} className="text-[11px] text-gold hover:underline flex items-center gap-1">
                        <FileText size={11} /> Perizia/documentazione <ExternalLink size={9} />
                    </a>
                    )}
                    {item.source_url && (
                        <a
                            href={item.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`aste-open-${item.id}`}
                            className="text-[11px] text-ink-400 hover:text-gold flex items-center gap-1"
                        >
                            <ExternalLink size={10} /> Apri asta originale
                        </a>
                    )}
                </div>
                <button
                    data-testid={`aste-match-badge-${item.id}`}
                    onClick={onOpenMatches}
                    disabled={!item.match_count}
                    className={`text-[11px] px-2.5 py-1 flex items-center gap-1.5 ${item.match_count ? "border border-gold/50 bg-gold/10 text-gold hover:bg-gold/20" : "border border-white/10 text-ink-500"}`}
                >
                    <Users size={11} /> MATCH: {item.match_count || 0} client{item.match_count === 1 ? "e" : "i"} interessat{item.match_count === 1 ? "o" : "i"}
                </button>
            </div>
        </div>
    );
}

function MatchesModal({ item, onClose }) {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get(`/crm/aste/${item.id}/matches`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
    }, [item.id]);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="aste-matches-modal">
            <div className="bg-ink-950 border border-gold/40 max-w-lg w-full p-7 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <span className="label-eyebrow text-gold">Clienti interessati</span>
                        <h3 className="font-serif text-xl text-ink-50 mt-1">{item.title}</h3>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                </div>
                {items === null ? (
                    <div className="text-ink-400 text-sm py-6 text-center">Caricamento…</div>
                ) : items.length === 0 ? (
                    <div className="text-ink-400 text-sm py-6 text-center">Nessun cliente compatibile trovato.</div>
                ) : (
                    <div className="space-y-2">
                        {items.map((l) => (
                            <div key={l.lead_id} data-testid={`aste-match-lead-${l.lead_id}`} className="border border-white/10 p-3 flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm text-ink-50">{l.name}</div>
                                    <div className="text-[11px] text-ink-400">{l.phone || l.email || "nessun contatto"}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gold border border-gold/40 px-2 py-0.5">{l.score}% match</span>
                                    {l.phone && <a href={`tel:${l.phone}`} className="text-xs cta-outline px-3 py-1.5">Chiama</a>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function CancelModal({ onClose, onConfirmed }) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const confirm = async () => {
        setSaving(true); setError("");
        try { await api.post("/crm/aste/cancel"); onConfirmed(); }
        catch (e) { setError(e?.response?.data?.detail || "Errore disdetta"); setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6" onClick={onClose}>
            <div className="bg-ink-950 border border-red-500/40 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-4"><AlertTriangle size={18} className="text-red-300" /><h3 className="font-serif text-xl text-ink-50">Disdici Radar Aste</h3></div>
                <p className="text-sm text-ink-100 mb-4">Il modulo resterà attivo fino alla fine del periodo già pagato, poi non si rinnoverà più.</p>
                {error && <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-200 text-xs">{error}</div>}
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 px-5 py-2.5 text-sm border border-white/15 text-ink-100 hover:border-white/30">Annulla</button>
                    <button data-testid="aste-cancel-confirm" onClick={confirm} disabled={saving} className="flex-1 px-5 py-2.5 text-sm bg-red-900/40 border border-red-500/60 text-red-100 hover:bg-red-900/60 disabled:opacity-50">
                        {saving ? "Attendere…" : "Confermo la disdetta"}
                    </button>
                </div>
            </div>
        </div>
    );
}
